import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { IDataStudioPark, IDataStudioSignal, IDataStudioDevice, ISeries } from 'app/data-studio-redesign/interfaces/data-studio.interface';
import { SIGNALS_TYPES_LIST } from 'app/data-studio-redesign/services/data-analysis-helper';

@Component({
    selector: 'app-new-scatter-plot',
    templateUrl: './new-scatter-plot.component.html',
    styleUrls: ['./new-scatter-plot.component.scss'],
    standalone: false
})

export class NewScatterPlotComponent implements OnChanges {

  @Input() parks: IDataStudioPark[];
  @Input() ids: string[];
  @Input() xUnit?: string;
  @Output() closeNew = new EventEmitter();
  @Output() createNew = new EventEmitter();

  public devicesList: Partial<IDataStudioDevice>[] = [];
  public signalsList: IDataStudioSignal[] = [];
  public selectedSignals: IDataStudioSignal[] = [];
  public series: ISeries[] = [];
  public filteredDevices: IDataStudioDevice[];
  public filteredSignals: IDataStudioSignal[];
  public parksNames: string[];
  public devicesListNames: string[];
  public selectedPark;
  public selectedDevice;
  public selectedDeviceY;
  public signalsXAxis;
  public signalsYAxis;
  public signalsXAxisNames: string[];
  public signalsYAxisNames: string[];
  public selectedSignalX;
  public selectedSignalY;
  public selectedXUnit: string;
  public addNewSignal: boolean = true;
  public sameDeviceBothSignals: boolean = true;

  public typesList = [...SIGNALS_TYPES_LIST];

  public get signalXLabel() {
    const unit = this.selectedSignalX?.unit ? `[${this.selectedSignalX.unit}]` : '';
    return this.selectedSignalX ? `${this.selectedSignalX.displayName} ${unit}` : 'DATA_STUDIO.DATA_PARAMETERS.SELECT_SIGNAL';
  }

  public get signalYLabel() {
    const unit = this.selectedSignalY?.unit ? `[${this.selectedSignalY.unit}]` : '';
    return this.selectedSignalY ? `${this.selectedSignalY.displayName} ${unit}` : 'DATA_STUDIO.DATA_PARAMETERS.SELECT_SIGNAL';
  }

  public get serieExist() {
    return this.ids?.includes(`${this.selectedDevice?.code}_${this.selectedSignalX?.name}_${this.selectedSignalY?.name}`)
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.parks?.currentValue) {
      this.parks.sort((a, b) => {
        return a.fe_name.localeCompare(b.fe_name);
      });
      this.parksNames = this.parks.map(park => park.fe_name);
    }
  }

  public close() {
    this.cleanSelection();
    this.closeNew.emit();
  }

  public addNewSeries() {
    this.createNew.emit({series: this.series, xUnit: this.selectedXUnit});
    this.cleanSelection();
  }

  public createSeries() {
    const selectedDeviceY = !!this.selectedDeviceY ? this.selectedDeviceY : this.selectedDevice;
    this.series.push({
      assetId: this.selectedDevice._id,
      assetIdY: selectedDeviceY._id,
      chartData: [],
      chartDataY: [],
      chartTypeName: 'Circle',
      color: `#${Math.floor(Math.random()*16777215).toString(16)}`,
      device: this.selectedDevice.code,
      deviceName: this.selectedDevice.name,
      deviceNameY: selectedDeviceY.name,
      deviceType: this.selectedDevice.deviceType,
      deviceTypeY: selectedDeviceY.deviceType,
      deviceY: selectedDeviceY.code,
      granularity: '1h',
      id: `${this.selectedDevice.code}_${selectedDeviceY.code}_${this.selectedSignalX.name}_${this.selectedSignalY.name}`,
      marker: {
        symbol: 'circle'
      },
      newItem: true,
      parkDisplayName: this.selectedDevice.parkName,
      parkId: this.selectedPark._id,
      parkName: this.selectedDevice.name,
      queryData: this.selectedDevice.queryData,
      signal: this.selectedSignalX.name,
      signalName: `${this.selectedSignalX.displayName}`,
      signalNameY: `${this.selectedSignalY.displayName}`,
      signalY: this.selectedSignalY.name,
      type:'scatter',
      unit: `${this.selectedSignalX.unit}`,
      unitY: `${this.selectedSignalY.unit}`,
      visible: false,
      yAxis: this.series?.length ? this.series.length + 1 : 1,
    });
    this.selectedPark = null;
    this.selectedDevice = null;
    this.selectedSignalX = null;
    this.selectedSignalY = null;
    this.addNewSignal = false;
  }

  public toggleAddNewSignal(): void {
    this.addNewSignal = true;
  }

  public selectPark({index}) {
    if (!this.sameDeviceBothSignals) {
      this.selectedDeviceY = null;
      this.selectedSignalY = null;
    }
    this.selectedPark = this.parks[index];
    this.selectedDevice = null;
    this.devicesList = this.parks[index].devices;
    this.devicesList = this.devicesList.sort((a, b) => a?.code.localeCompare(b?.code, undefined, {numeric: true, sensitivity: 'base'}));
    this.devicesListNames = this.devicesList.map(device => device.code.replace(new RegExp('_', 'g'), ' '));
  }

  public selectDevice({index}) {
    const xUnit = this.xUnit || this.selectedXUnit;
    this.selectedDevice = this.devicesList[index];
    this.selectedSignalX = null;
    this.signalsList = this.devicesList[index].signalsList;
    this.signalsXAxis = this.selectedPark.name === this.selectedDevice.name ? this.signalsList : xUnit ? this.signalsList.filter(signal => signal.unit === xUnit) : this.signalsList;
    this.signalsXAxis = this.signalsXAxis.sort((a, b) => a?.displayName.localeCompare(b?.displayName, undefined, {numeric: true, sensitivity: 'base'}));
    this.signalsXAxisNames = this.signalsXAxis.map(signal => signal.displayName);
  }

  public selectDeviceY({option: deviceName}) {
    const index = this.devicesListNames.findIndex(a => a === deviceName);
    this.selectedDeviceY = this.devicesList[index];
    this.selectedSignalY = null;
    this.updateSignalsYList();
  }

  public selectSignalX({index}) {
    this.selectedSignalX = this.signalsXAxis[index];
    this.signalsYAxis = this.signalsList.filter(signal => signal.displayName !== this.selectedSignalX.name && signal.unit !== this.selectedSignalX.unit);
    this.signalsYAxis = this.signalsList.filter(signal => signal.name !== this.selectedSignalX.name);
    this.selectedXUnit = this.xUnit || this.selectedSignalX.unit;
    if (this.series?.length) {
      this.series.forEach(serie => {
        if (serie.parkDisplayName === this.selectedDevice.parkName && 
            serie.device === this.selectedDevice.code && 
            serie.signalName === `${this.selectedSignalX.displayName} [${this.selectedSignalX.unit}]`
        ) {
          this.signalsYAxis = this.signalsYAxis.filter(signal => serie.signalNameY !== `${signal.displayName} [${signal.unit}]`);
        }
      })
    }
    this.signalsYAxis = this.signalsYAxis.sort((a, b) => a?.displayName.localeCompare(b?.displayName, undefined, {numeric: true, sensitivity: 'base'}))
    this.signalsYAxisNames = this.signalsYAxis.map(signal => signal.displayName);
  }

  public selectSignalY({index}) {
    this.selectedSignalY = this.signalsYAxis[index];
    this.signalsYAxisNames = this.signalsYAxis.map(signal => signal.displayName);
  }

  public deleteSerie(deletedSerie) {
    this.series = this.series.filter(serie => serie.id !== deletedSerie.id);
    this.series.forEach((serie, index) => {
      serie.yAxis = index;
      return serie;
    });
    if (this.series.length === 0) {
      this.selectedXUnit = undefined;
      this.selectedDevice = null;
      this.selectedSignalX = null;
      this.selectedSignalY = null;
    }
  }

  public toggleSameDeviceBothSignals(): void {
    this.sameDeviceBothSignals = !this.sameDeviceBothSignals;

    if (!this.selectedPark) return;
    this.selectedDeviceY = this.sameDeviceBothSignals ? structuredClone(this.selectedDevice) : null;
    if (!this.sameDeviceBothSignals) {
      this.selectedDeviceY = null;
      this.selectedSignalY = null;
    } else {
      this.selectedDeviceY = structuredClone(this.selectedDevice);
    }
  }

  private updateSignalsYList() {
    if (!this.selectedDeviceY) {
      this.signalsYAxis = null;
      return;
    }

    const signalsList = this.selectedDeviceY.signalsList;
    this.signalsYAxis = signalsList;
    if (this.sameDeviceBothSignals && this.selectedSignalX) this.signalsYAxis = this.signalsYAxis.filter(signal => signal.name !== this.selectedSignalX.name || signal.displayName !== this.selectedSignalX.displayName);
    this.signalsYAxis = this.signalsYAxis.sort((a, b) => a?.displayName.localeCompare(b?.displayName, undefined, {numeric: true, sensitivity: 'base'}))
    this.signalsYAxisNames = this.signalsYAxis.map(signal => signal.displayName);
  }

  private cleanSelection() {
    this.selectedPark = null;
    this.selectedDevice = null;
    this.selectedSignalX = null;
    this.selectedSignalY = null;
    this.devicesList = [];
    this.signalsList = [];
    this.signalsXAxis = [];
    this.signalsYAxis = [];
    this.series = [];
  }

}
